<script setup lang="ts">
import { computed, type Ref, ref, watch } from 'vue'

import { BoxComponent, Card, MultiAddSelector, ProfileOptionItem, TypographyComponent } from '..'
import { type CreatorsV2Filter, type SelectedCreatorRef, type UserItem } from '../../types'
import { debounce } from '../../utils'

type UseLoadCreatorsReturn = {
  data: Ref<UserItem[]>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
}

type UseMatchCreatorsReturn = {
  data: Ref<{ matched: UserItem[]; unmatched: string[] }>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
}

type CreatorOption = UserItem & { label: string }

const props = withDefaults(
  defineProps<{
    placeholder?: string
    advertiserGroupId?: number
    advertiserId?: number
    hideEmail?: boolean
    width?: string | number
    loadCreators: (filterRef: Ref<CreatorsV2Filter>) => UseLoadCreatorsReturn
    matchCreators: (advertiserGroupId: Ref<number | undefined>, tokens: Ref<string[]>) => UseMatchCreatorsReturn
  }>(),
  {
    placeholder: 'Search creators',
    hideEmail: false,
  },
)

const emit = defineEmits<{
  'unmatched-tokens': [tokens: string[]]
  add: [creators: UserItem[]]
}>()

const selectedCreators = defineModel<SelectedCreatorRef[]>({ default: () => [] })

/** The model carries ids only; hosts that want the whole creator listen to `add`. */
const toSelectedRef = ({ id }: UserItem): SelectedCreatorRef => ({ id })

const inputValue = ref('')
const filter = ref<CreatorsV2Filter>({
  page: 1,
  ...(props.advertiserGroupId && {
    advertiserGroup: {
      id: props.advertiserGroupId,
      name: '',
    },
  }),
  ...(props.advertiserId && {
    advertiser: {
      id: props.advertiserId,
      label: '',
    },
  }),
  query: '',
})
const matchTokens = ref<string[]>([])
const addedIds = ref<Set<number>>(new Set())
const unmatchedTokens = ref<string[]>([])
const matchFailed = ref(false)

const { data: suggestions, isLoading, error: loadError } = props.loadCreators(filter)
const advertiserGroupIdRef = computed(() => props.advertiserGroupId)
const { data: matchResult, isLoading: isMatching, error: matchError } = props.matchCreators(advertiserGroupIdRef, matchTokens)
const searchFailed = computed(() => Boolean(loadError.value))

const creatorOptions = computed<CreatorOption[]>(() =>
  matchTokens.value.length ? [] : suggestions.value.map((item) => ({ ...item, label: item.username || `ID:${item.id}` })),
)

const handleSearchQuery = debounce((value: string) => (filter.value.query = value), 500)
const debouncedMatchTokens = debounce((tokens: string[]) => (matchTokens.value = tokens), 500)

const handleSearch = (value: string) => {
  debouncedMatchTokens.cancel()
  unmatchedTokens.value = []
  if (matchFailed.value) {
    matchFailed.value = false
    matchTokens.value = []
  }
  handleSearchQuery(value)
}

const handleBulkSearch = (tokens: string[]) => {
  handleSearchQuery.cancel()
  filter.value.query = ''
  debouncedMatchTokens(tokens)
}

const handleAdd = (id: number) => {
  const creator = suggestions.value.find((item) => item.id === id)
  if (!creator || addedIds.value.has(creator.id)) {
    return
  }
  const isNew = !selectedCreators.value.some((item) => item.id === creator.id)
  if (isNew) {
    selectedCreators.value = [...selectedCreators.value, toSelectedRef(creator)]
  }
  addedIds.value = new Set(addedIds.value).add(creator.id)
  if (isNew) {
    emit('add', [creator])
  }
}

const allAddedIds = computed(() => [...new Set([...addedIds.value, ...selectedCreators.value.map((item) => item.id)])])

const handleVisibleChange = (visible: boolean) => {
  if (!visible) {
    inputValue.value = ''
    handleSearchQuery.cancel()
    debouncedMatchTokens.cancel()
    filter.value.query = ''
    matchTokens.value = []
    unmatchedTokens.value = []
    addedIds.value = new Set()
    matchFailed.value = false
  }
}

watch(isMatching, (loading) => {
  if (loading || !matchTokens.value.length) {
    return
  }
  matchFailed.value = Boolean(matchError.value)
  if (matchFailed.value) {
    return
  }
  inputValue.value = ''
  matchTokens.value = []
})

watch(
  matchResult,
  (result) => {
    const newlyMatched = result.matched.reduce<UserItem[]>(
      (acc, user) =>
        selectedCreators.value.some((item) => item.id === user.id) || acc.some((item) => item.id === user.id) ? acc : [...acc, user],
      [],
    )
    if (newlyMatched.length) {
      selectedCreators.value = [...selectedCreators.value, ...newlyMatched.map(toSelectedRef)]
      emit('add', newlyMatched)
    }
    unmatchedTokens.value = result.unmatched
    emit('unmatched-tokens', result.unmatched)
  },
  { deep: true },
)

watch(
  () => [props.advertiserGroupId, props.advertiserId],
  ([advertiserGroupId, advertiserId]) => {
    filter.value.query = ''
    filter.value.advertiserGroup = advertiserGroupId ? { id: advertiserGroupId, name: '' } : undefined
    filter.value.advertiser = advertiserId ? { id: advertiserId, label: '' } : undefined
  },
)
</script>

<template>
  <Card title="Add creators manually">
    <MultiAddSelector
      v-model:query="inputValue"
      :options="creatorOptions"
      :placeholder="placeholder"
      :loading="isLoading || isMatching"
      :width="width"
      :added-ids="allAddedIds"
      :hide-empty-state="matchFailed || searchFailed"
      empty-text="No matching creators"
      @search="handleSearch"
      @bulk-search="handleBulkSearch"
      @add="handleAdd($event.id)"
      @visible-change="handleVisibleChange"
    >
      <template #status>
        <BoxComponent v-if="matchFailed || searchFailed || unmatchedTokens.length" :p="16">
          <TypographyComponent v-if="searchFailed" variant="body-small" color="var(--error)">
            Couldn't search creators. Please try again.
          </TypographyComponent>
          <TypographyComponent v-if="matchFailed" variant="body-small" color="var(--error)">
            Couldn't look those creators up. Please try again.
          </TypographyComponent>
          <TypographyComponent v-if="unmatchedTokens.length" variant="body-small" color="var(--error)">
            Couldn't find {{ unmatchedTokens.length }} creator{{ unmatchedTokens.length > 1 ? 's' : '' }}:
            {{ unmatchedTokens.join(', ') }}
          </TypographyComponent>
        </BoxComponent>
      </template>
      <template #option="{ item, added }">
        <ProfileOptionItem
          :id="item.id"
          :username="(item as CreatorOption).username"
          :email="hideEmail ? undefined : (item as CreatorOption).email"
          :image="(item as CreatorOption).image"
          :avatar-name="(item as CreatorOption).displayName"
          :added="added"
        />
      </template>
    </MultiAddSelector>
  </Card>
</template>
